import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Trash2, Truck, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PageHeader, StatusBadge } from "@/components/Common";
import { fmtMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function emptyLine() {
  return { item_id: "", name: "", specification: "", unit: "", qty: 1, unit_price: 0, line_total: 0 };
}

export default function OrderForm() {
  const { t } = useI18n();
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: api.listClients });
  const { data: items = [] } = useQuery({ queryKey: ["items"], queryFn: api.listItems });
  const { data: order } = useQuery({
    queryKey: ["order", id],
    queryFn: () => api.getOrder(id),
    enabled: isEdit,
  });

  const [clientId, setClientId] = useState("");
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState("");
  const [statusVal, setStatusVal] = useState("draft");
  const [lines, setLines] = useState([emptyLine()]);

  useEffect(() => {
    if (order) {
      setClientId(order.client_id || "");
      setTaxRate(order.tax_rate || 0);
      setNotes(order.notes || "");
      setStatusVal(order.status || "draft");
      setLines(
        (order.items || []).map((l) => ({
          item_id: l.item_id,
          name: l.name,
          specification: l.specification || "",
          unit: l.unit || "",
          qty: l.qty,
          unit_price: l.unit_price,
          line_total: l.line_total,
        }))
      );
    }
  }, [order]);

  const itemsMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const isReadOnly = isEdit && order?.status === "converted";

  function updateLine(idx, patch) {
    setLines((prev) => {
      const next = [...prev];
      const cur = { ...next[idx], ...patch };
      cur.line_total = +(Number(cur.qty || 0) * Number(cur.unit_price || 0)).toFixed(2);
      next[idx] = cur;
      return next;
    });
  }
  function selectItemForLine(idx, item_id) {
    const it = itemsMap[item_id];
    if (!it) return updateLine(idx, { item_id });
    updateLine(idx, {
      item_id,
      name: it.name,
      specification: it.specification,
      unit: it.unit,
      unit_price: Number(it.price || 0),
    });
  }
  function addLine() { setLines((p) => [...p, emptyLine()]); }
  function removeLine(idx) { setLines((p) => p.filter((_, i) => i !== idx)); }

  const subtotal = lines.reduce((s, l) => s + Number(l.line_total || 0), 0);
  const tax = +(subtotal * (Number(taxRate || 0) / 100)).toFixed(2);
  const total = +(subtotal + tax).toFixed(2);

  const saveMut = useMutation({
    mutationFn: (body) => (isEdit ? api.updateOrder(id, body) : api.createOrder(body)),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(isEdit ? "Order updated" : `Order created: ${data.order_number}`);
      nav(`/orders`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });

  const convertMut = useMutation({
    mutationFn: () => api.convertOrder(id, {}),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["delivery-orders"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`DO created: ${data.do_number}`);
      nav(`/delivery-orders/${data.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });

  const invoiceMut = useMutation({
    mutationFn: () => api.createInvoiceFromOrder(id, {}),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      toast.success(`Invoice created: ${data.invoice_number}`);
      nav(`/invoices/${data.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });

  function submit(e) {
    e.preventDefault();
    if (!clientId) { toast.error(t("select_client")); return; }
    const valid = lines.filter((l) => l.item_id && Number(l.qty) > 0);
    if (valid.length === 0) { toast.error(t("add_line")); return; }
    saveMut.mutate({
      client_id: clientId,
      tax_rate: Number(taxRate || 0),
      notes,
      status: statusVal,
      items: valid.map((l) => ({
        item_id: l.item_id,
        qty: Number(l.qty),
        unit_price: Number(l.unit_price),
        specification: l.specification,
      })),
    });
  }

  return (
    <div data-testid="order-form-page">
      <Link to="/orders" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4">
        <ArrowLeft className="size-3.5 mr-1" /> {t("orders")}
      </Link>
      <PageHeader
        overline={isEdit ? t("order_number") + " " + (order?.order_number || "") : t("new_order")}
        title={isEdit ? (order?.order_number || t("orders")) : t("new_order")}
        actions={
          <>
            {isEdit && order?.status && <StatusBadge value={order.status} />}
            {isEdit && order?.delivery_order_id ? (
              <Link to={`/delivery-orders/${order.delivery_order_id}`}>
                <Button data-testid="goto-do-btn" variant="outline">
                  <Truck className="size-4 mr-1.5" /> DO
                </Button>
              </Link>
            ) : isEdit && !isReadOnly ? (
              <Button
                data-testid="convert-order-btn"
                variant="outline"
                onClick={() => convertMut.mutate()}
                disabled={convertMut.isPending}
              >
                <Truck className="size-4 mr-1.5" /> {t("convert_to_do")}
              </Button>
            ) : null}
            {isEdit && order?.invoice_id ? (
              <Link to={`/invoices/${order.invoice_id}`}>
                <Button data-testid="goto-invoice-btn" variant="outline">
                  {t("invoice")}
                </Button>
              </Link>
            ) : isEdit ? (
              <Button
                data-testid="create-invoice-from-order-btn"
                variant="outline"
                onClick={() => invoiceMut.mutate()}
                disabled={invoiceMut.isPending}
              >
                {t("create_invoice")}
              </Button>
            ) : null}
            {!isReadOnly && (
              <Button
                data-testid="save-order-btn"
                onClick={submit}
                className="bg-primary hover:bg-primary/90"
                disabled={saveMut.isPending}
              >
                <Save className="size-4 mr-1.5" /> {t("save")}
              </Button>
            )}
          </>
        }
      />

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {(() => {
            // Aggregate stock shortages across all lines for a single banner.
            const shortages = lines
              .map((l, i) => {
                if (!l.item_id) return null;
                const src = items.find((x) => x.id === l.item_id);
                const stock = Number(src?.qty ?? 0);
                const want = Number(l.qty || 0);
                const short = want - stock;
                return short > 0 ? { i, name: src?.name || l.name, stock, want, short } : null;
              })
              .filter(Boolean);
            if (!shortages.length) return null;
            return (
              <div data-testid="stock-warning-banner"
                   className="border border-amber-200 bg-amber-50 text-amber-900 rounded-md p-3 flex gap-3 items-start">
                <AlertTriangle className="size-4 mt-0.5 shrink-0" />
                <div className="text-xs leading-relaxed">
                  <div className="font-semibold">Some lines exceed available stock — back-order may be needed.</div>
                  <ul className="mt-1 list-disc list-inside">
                    {shortages.map((s) => (
                      <li key={s.i}>
                        <span className="font-medium">{s.name}</span>: ordering <span className="mono">{s.want}</span>, stock <span className="mono">{s.stock}</span> — short by <span className="mono font-semibold">{s.short}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })()}
          <div className="border border-border rounded-md bg-white p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="overline">{t("client")} *</Label>
                <Select value={clientId} onValueChange={setClientId} disabled={isReadOnly}>
                  <SelectTrigger data-testid="order-client-select"><SelectValue placeholder={t("select_client")} /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`order-client-opt-${c.id}`}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">{t("status")}</Label>
                <Select value={statusVal} onValueChange={setStatusVal} disabled={isReadOnly}>
                  <SelectTrigger data-testid="order-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">{t("order_status_draft")}</SelectItem>
                    <SelectItem value="confirmed">{t("order_status_confirmed")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="border border-border rounded-md bg-white">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <h3 className="font-display font-semibold">Items</h3>
              {!isReadOnly && (
                <Button type="button" data-testid="add-line-btn" size="sm" variant="outline" onClick={addLine}>
                  <Plus className="size-3.5 mr-1.5" /> {t("add_line")}
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-b border-border">
                    <TableHead className="overline">{t("item_name")}</TableHead>
                    <TableHead className="overline">{t("specification")}</TableHead>
                    <TableHead className="overline w-20 text-right">{t("qty")}</TableHead>
                    <TableHead className="overline w-16 text-right">{t("unit")}</TableHead>
                    <TableHead className="overline w-28 text-right">{t("price")}</TableHead>
                    <TableHead className="overline w-28 text-right">{t("line_total")}</TableHead>
                    {!isReadOnly && <TableHead className="w-10"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l, idx) => (
                    <TableRow key={idx} data-testid={`order-line-${idx}`}>
                      <TableCell className="min-w-[200px]">
                        <Select
                          value={l.item_id || undefined}
                          onValueChange={(v) => selectItemForLine(idx, v)}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger data-testid={`line-item-select-${idx}`}>
                            <SelectValue placeholder={t("select_item")} />
                          </SelectTrigger>
                          <SelectContent>
                            {items.map((it) => (
                              <SelectItem key={it.id} value={it.id}>
                                {it.name} {it.sku ? `· ${it.sku}` : ""} · <span className="text-muted-foreground">stock {it.qty ?? 0}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {l.item_id && (() => {
                          const src = items.find((x) => x.id === l.item_id);
                          const stock = Number(src?.qty ?? 0);
                          const want = Number(l.qty || 0);
                          const short = want - stock;
                          return (
                            <div className="mt-1 flex items-center gap-2 text-[11px]">
                              <span className={`mono ${short > 0 ? "text-amber-700 font-semibold" : "text-muted-foreground"}`}>
                                In stock: {stock}
                              </span>
                              {short > 0 && (
                                <span
                                  data-testid={`stock-warning-${idx}`}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200">
                                  <AlertTriangle className="size-3" />
                                  Short by {short}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="min-w-[200px]">
                        <Input
                          data-testid={`line-spec-${idx}`}
                          value={l.specification}
                          onChange={(e) => updateLine(idx, { specification: e.target.value })}
                          disabled={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          data-testid={`line-qty-${idx}`}
                          type="number" min="0" step="1"
                          className="text-right mono"
                          value={l.qty}
                          onChange={(e) => updateLine(idx, { qty: e.target.value })}
                          disabled={isReadOnly}
                        />
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground mono">{l.unit || "—"}</TableCell>
                      <TableCell>
                        <Input
                          data-testid={`line-price-${idx}`}
                          type="number" min="0" step="0.01"
                          className="text-right mono"
                          value={l.unit_price}
                          onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                          disabled={isReadOnly}
                        />
                      </TableCell>
                      <TableCell className="text-right mono font-medium">{fmtMoney(l.line_total)}</TableCell>
                      {!isReadOnly && (
                        <TableCell>
                          <Button
                            data-testid={`line-remove-${idx}`}
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLine(idx)}
                            className="text-red-600"
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="border border-border rounded-md bg-white p-5">
            <h3 className="overline mb-3">Totals</h3>
            <div className="space-y-2.5 text-sm mono">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("subtotal")}</span>
                <span data-testid="order-subtotal">{fmtMoney(subtotal)}</span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-muted-foreground">{t("tax_rate")}</span>
                <Input
                  data-testid="order-tax-rate"
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-24 h-8 text-right"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("tax")}</span>
                <span data-testid="order-tax">{fmtMoney(tax)}</span>
              </div>
              <div className="border-t border-border pt-2.5 flex justify-between text-base font-bold text-primary">
                <span>{t("total")}</span>
                <span data-testid="order-total">{fmtMoney(total)}</span>
              </div>
            </div>
          </div>

          <div className="border border-border rounded-md bg-white p-5">
            <Label className="overline">{t("notes")}</Label>
            <Textarea
              data-testid="order-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              disabled={isReadOnly}
              className="mt-2"
            />
          </div>
        </div>
      </form>
    </div>
  );
}
